import cookies from "js-cookie"
import Settings from "./CarrotConfig"

export const enum OneTrustCookieCategory {
  StrictlyNecessary = "C0001",
  Performance = "C0002",
  Functional = "C0003",
  Targeting = "C0004",
  SocialMedia = "C0005"
}

declare global {
  interface Window {
    OptanonWrapper: { __loaded: boolean }
    OneTrust: {
      OnConsentChanged: (callback: () => void) => void
      RejectAll: () => void
      changeLanguage: (lang: string) => void
    }
  }
}

const consentByCookieCategory = new Map<OneTrustCookieCategory, boolean>()

const CONSENT_COOKIE_NAME = "OptanonConsent"
const CONSENT_COOKIE_GROUPS_PARAM_NAME = "groups"
const CONSENT_COOKIE_GROUPS_SEPARATOR = ","
const CONSENT_COOKIE_GROUP_NAME_VALUE_SEPARATOR = ":"

const enum CookieCategoryConsent {
  Accepted = "1",
  Rejected = "0"
}

function updateConsent(): void {
  const consentData = cookies.get(CONSENT_COOKIE_NAME)
  const consentParams = new URLSearchParams(consentData)
  const consentGroups = consentParams.get(CONSENT_COOKIE_GROUPS_PARAM_NAME)

  consentGroups?.split(CONSENT_COOKIE_GROUPS_SEPARATOR)?.forEach((group) => {
    const [category, consent] = group?.split(CONSENT_COOKIE_GROUP_NAME_VALUE_SEPARATOR) || []
    consentByCookieCategory.set(category as OneTrustCookieCategory, consent === CookieCategoryConsent.Accepted)
  })
}

const autoRejectCookieConsent = Settings.AUTO_REJECT_ONE_TRUST_COOKIE_CONSENT ?? false

function hideOneTrustBannerOnStage(): void {
  // Stage runs the production OneTrust config, which is domain-locked to app.get-carrot.com,
  // so on app-stage.get-carrot.com consent can't persist and the banner reappears on every
  // page load. OneTrust has no config for the stage domain (and one can't be added), so we
  // hide the banner with CSS — OneTrust still loads and blocks cookies, and production is
  // unaffected (gated on the stage hostname).
  // Background + approaches we ruled out: https://carrotfertility.atlassian.net/wiki/spaces/EN/pages/4539645988
  const STAGE_HOSTNAME = "app-stage.get-carrot.com"

  if (window.location.hostname !== STAGE_HOSTNAME) {
    return
  }

  const style = document.createElement("style")
  style.textContent = "#onetrust-banner-sdk { display: none !important; }"
  document.head.appendChild(style)
}

hideOneTrustBannerOnStage()

const initOneTrust = (): void => {
  if (autoRejectCookieConsent) {
    window.OneTrust.RejectAll()
  }

  updateConsent()
  window.OneTrust.OnConsentChanged(updateConsent)
}

const oneTrustLoadedBeforeApp = window.OptanonWrapper?.__loaded

if (oneTrustLoadedBeforeApp) {
  initOneTrust()
} else {
  window.addEventListener("OptanonWrapperLoaded", initOneTrust)
}

export function isOneTrustCookieCategoryAccepted(category: OneTrustCookieCategory): boolean {
  return consentByCookieCategory.get(category)
}
